import { ContextCollectionHost } from "../components/ContextCollectionHost";
import { NeucleosContextSuggestions } from "../components/NeucleosContextSuggestions";
import { BoardCompanionProvider } from "../components/BoardCompanion";
import { ActiveConversationContext } from "../components/cleoPanelControls";
import { CrmContextCoordinate } from "../neucleos/crmContext";
import { railSessionName } from "../neucleos/railSessionName";
import { FilesBoardDoorwayContext, type FilesBoardDoorway } from "../neucleos/filesBoardContext";
import { parseNotificationDestination } from "../neucleos/notifications";
import { contextChangeRefusalCopy } from "../neucleos/refusalCopy";
import { openWorldViewInboxRail } from "../neucleos/worldViewController";
import { owedWorldNotice, settleWorldNotice } from "../neucleos/openWorldNotice";
import { deckFrontStands, requestDeckFront } from "../neucleos/boardDeckSync";
import { confirmedWorldRoom } from "../neucleos/confirmedWorldRoom";
import { DockAccountControls } from "~/components/DockAccountControls";
import { WorldNotAnsweringNotice } from "~/components/WorldNotAnsweringNotice";
import {
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useAtomValue } from "@effect/atom-react";
import * as Schema from "effect/Schema";
import { useEffect, useLayoutEffect, useMemo, useCallback, useRef, useState } from "react";

import { isCommandPaletteOpen } from "../commandPaletteBus";
import { useClientSettings } from "../hooks/useSettings";
import { openCommandPalette } from "../commandPaletteBus";
import { BoundaryDropZones } from "../components/BoundaryDropZones";
import {
  NeucleosContextRail,
  type MovePredicateDoorway,
  type RequestPageDoorway,
  type ContextMemberMove,
} from "../components/NeucleosContextRail";
import { installDocumentDropGuard } from "../neucleos/memberMoveDrag";
import { scopeThreadRef } from "@cleo/client-runtime/environment";
import {
  useProjects,
  useThreadActivities,
  useThreadMessages,
  useThreadShell,
  useThreadShells,
} from "../state/entities";
import { usePrimaryEnvironmentId } from "../state/environments";
import { selectProjectGroupingSettings } from "../logicalProject";
import { buildSidebarProjectSnapshots } from "../sidebarProjectGrouping";
import { dispatchPreviewAction } from "../components/preview/previewActionBus";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { startNewThreadFromContext } from "../lib/chatThreadActions";
import { isPreviewFocused } from "../lib/previewFocus";
import { isTerminalFocused } from "../lib/terminalFocus";
import { resolveShortcutCommand } from "../keybindings";
import { selectThreadTerminalUiState, useTerminalUiStateStore } from "../terminalUiStateStore";
import { isPreviewSupportedInRuntime } from "../previewStateStore";
import {
  selectActiveBoardDeck,
  selectThreadBoardDeckState,
  useBoardDeckStore,
} from "../boardDeckStore";
import { useThreadSelectionStore } from "../threadSelectionStore";
import { stackedThreadToast, toastManager } from "~/components/ui/toast";
import { primaryServerKeybindingsAtom, serverEnvironment } from "~/state/server";
import { roomMemberType, workingContextMemberIds } from "@cleo/contracts";
import { onOpenMemberRequest, type OpenMemberRequest } from "~/neucleos/openMemberBridge";
import { isElectron } from "~/env";
import { shellOpensFor } from "../authGate";
import { readLocalApi } from "~/localApi";
import { useAtomCommand } from "~/state/use-atom-command";
import { NeucleosNestedSessionStrip } from "../components/NeucleosRoomFrame";
import { GovernedWorldViewGate } from "../components/WorldViewStateFace";
import { SidebarInset } from "../components/ui/sidebar";
import {
  AGENT_RAIL_DEFAULT_WIDTH,
  AGENT_RAIL_MAX_WIDTH,
  AGENT_RAIL_MIN_WIDTH,
  AGENT_RAIL_WIDTH_STORAGE_KEY,
  PRIMARY_AGENT_RAIL_DEFAULT_WIDTH,
  PRIMARY_AGENT_RAIL_WIDTH_STORAGE_KEY,
} from "../components/agentRailWidth";
import { planDeskWidth, useCleoWideSetting, useDeskWidthStore } from "../components/cleoWide";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useStageWidth } from "../hooks/useStageWidth";
import { resolveMemberCanvasScope } from "../neucleos/memberCanvasScope";
import { localModeActive } from "../neucleos/worldSelection";
import {
  useLocalBoundary,
  moveLocalContext,
  browseLocalDirectory,
  type LocalContextAddress,
} from "../neucleos/localBoundaryClient";
import { DraftId, useComposerDraftSessionStore } from "../composerDraftStore";
import { rememberWorldRoom, useNeucleosWorldStore } from "../neucleos/worldStore";
import { projectWorldSessionLineage, useWorldView } from "../neucleos/worldView";
import { useRootSessionThreadInputs } from "../rootSessionStripContainer";
import {
  projectPagedRoomUniverse,
  projectStoredSelection,
  projectRoomUniverseMembers,
  projectRoomUniverseSkeleton,
  renderableUniverseGroups,
  type RoomUniverseProjection,
} from "../neucleos/activeAgentCoordinate";
import {
  roomCatalogKey,
  roomCatalogDisplayEntry,
  roomUniverseDisplayEntry,
  roomUniverseCoordinateKey,
  useRoomUniverseStore,
} from "../neucleos/roomUniverseStore";
import { initializeBoardContextBirth } from "../neucleos/boardContextBirth";
import { useRoomUniverseRefresh } from "../neucleos/useRoomUniverseRefresh";
import {
  skeletonCodeRootLabel,
  useWorldWorkspaceBindings,
} from "../neucleos/worldWorkspaceBinding";
import { resolveAddressSessionId } from "../neucleos/agentRailAddress";
import {
  resolveDisplayedConversation,
  resolveRouteConversation,
  resolveShellCoordinates,
  type ShellCoordinates,
} from "../neucleos/displayedConversation";
import { useDoorSessionEntries } from "../neucleos/useDoorSessionEntries";
import { openWorldRoomConnections } from "../neucleos/worldViewController";
import { projectContextDelivery, projectWorkingContextDelivery } from "../neucleos/contextDelivery";
import {
  completedTurnKey,
  markContextProposalRelevance,
  readContextProposal,
} from "../neucleos/contextProposalClient";
import type {
  ContextProposalItemRef,
  ContextProposalProjection,
  RelevanceMarkKind,
} from "../components/NeucleosContextRailProposalZone";
import { parseSessionSessionLeafPath, worldViewFrameKind } from "../worldViewRoute";

function ChatRouteGlobalShortcuts() {
  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const selectedThreadKeysSize = useThreadSelectionStore((state) => state.selectedThreadKeys.size);
  const { activeDraftThread, activeThread, defaultProjectRef, handleNewThread, routeThreadRef } =
    useHandleNewThread();
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const projects = useProjects();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const projectGroupCount = useMemo(
    () =>
      buildSidebarProjectSnapshots({
        projects,
        settings: projectGroupingSettings,
        primaryEnvironmentId,
        resolveEnvironmentLabel: () => null,
      }).length,
    [primaryEnvironmentId, projectGroupingSettings, projects],
  );
  const terminalOpen = useTerminalUiStateStore((state) =>
    routeThreadRef
      ? selectThreadTerminalUiState(state.terminalUiStateByThreadKey, routeThreadRef).terminalOpen
      : false,
  );
  // The `previewOpen` shortcut-context flag here uses the store-only value;
  // the URL-aware arbitration lives inside ChatView's `onTogglePreview`,
  // which we invoke via the action bus to avoid duplicating the rule.
  const previewOpen = useBoardDeckStore((state) =>
    routeThreadRef ? selectActiveBoardDeck(state.byThreadKey, routeThreadRef) === "preview" : false,
  );
  useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen,
          previewFocus: isPreviewFocused(),
          previewOpen,
        },
      });

      if (isCommandPaletteOpen()) {
        return;
      }

      if (event.key === "Escape" && selectedThreadKeysSize > 0) {
        event.preventDefault();
        clearSelection();
        return;
      }

      if (command === "chat.newLocal") {
        event.preventDefault();
        event.stopPropagation();
        void startNewThreadFromContext({
          activeDraftThread,
          activeThread: activeThread ?? undefined,
          defaultProjectRef,
          handleNewThread,
        });
        return;
      }

      if (command === "chat.new") {
        event.preventDefault();
        event.stopPropagation();
        // Creation routes through the command palette whenever there is a
        // real choice to make; single-project setups keep the immediate
        // contextual create.
        if (projectGroupCount > 1) {
          openCommandPalette({ open: "new-thread-in" });
          return;
        }
        void startNewThreadFromContext({
          activeDraftThread,
          activeThread: activeThread ?? undefined,
          defaultProjectRef,
          handleNewThread,
        });
        return;
      }

      if (command === "preview.toggle") {
        event.preventDefault();
        event.stopPropagation();
        if (!routeThreadRef) return;
        if (!isPreviewSupportedInRuntime()) {
          toastManager.add(
            stackedThreadToast({
              type: "info",
              title: "Preview is desktop-only",
              description: "Open Cleo in the desktop app to use the in-app preview.",
            }),
          );
          return;
        }
        dispatchPreviewAction("toggle-panel");
        return;
      }

      // The remaining preview commands only fire when the panel is the
      // currently-focused tenant. The `when: previewFocus` rule already
      // gates this, but defend against the keybinding being misconfigured.
      if (
        command === "preview.refresh" ||
        command === "preview.focusUrl" ||
        command === "preview.zoomIn" ||
        command === "preview.zoomOut" ||
        command === "preview.resetZoom"
      ) {
        event.preventDefault();
        event.stopPropagation();
        const action =
          command === "preview.refresh"
            ? "refresh"
            : command === "preview.focusUrl"
              ? "focus-url"
              : command === "preview.zoomIn"
                ? "zoom-in"
                : command === "preview.zoomOut"
                  ? "zoom-out"
                  : "reset-zoom";
        dispatchPreviewAction(action);
      }
    };

    window.addEventListener("keydown", onWindowKeyDown);
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown);
    };
  }, [
    activeDraftThread,
    activeThread,
    clearSelection,
    handleNewThread,
    keybindings,
    defaultProjectRef,
    previewOpen,
    projectGroupCount,
    routeThreadRef,
    selectedThreadKeysSize,
    terminalOpen,
  ]);

  return null;
}

/**
 * Which frame a _chat leaf gets. Session leaves (one thread or draft) share
 * one SidebarInset owned by this layout, with the root-session strip above
 * the leaf. Non-session leaves — the index landing among them — keep their
 * own frames: they are not sessions and must never grow a session tab
 * (PLAN.md Arc A1).
 */
export const chatLayoutFrameKind = worldViewFrameKind;

function ChatSessionFrame({ pathname }: { readonly pathname: string }) {
  const leaf = useMemo(() => parseSessionSessionLeafPath(pathname), [pathname]);
  const contextRailDensity = useClientSettings((settings) => settings.density);
  // The `?a=` view state (RAIL-5b): the rail follows the ADDRESSED session,
  // which may be a lineage ancestor fronted inside a host rail (PR13/CX15).
  const routeSearch = useLocation({ select: (location) => location.search }) as Record<
    string,
    unknown
  >;
  const requestedAddress =
    typeof routeSearch["a"] === "string" && routeSearch["a"].length > 0
      ? (routeSearch["a"] as string)
      : undefined;
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const worldsLoaded = useNeucleosWorldStore((state) => state.loaded);
  // RL-CAN-23: a fresh in-app sign-in replaces the grant without navigating.
  // The Context universe must consume the same revision signal as the World
  // room or an earlier refusal remains cached until the person switches Worlds.
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  // The ONE mode derivation (F04): a zero-World or signed-out Actor is Local
  // (W06), so their room-universe absence reads "local", never "no-world".
  const localMode = useNeucleosWorldStore((state) =>
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    }),
  );
  const shells = useThreadShells();
  const sessionRoom = useWorldView();
  const bindingsByEnvironment = useWorldWorkspaceBindings();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const conversationRoute = JSON.stringify([activeWorldKey, pathname, requestedAddress]);
  const [ownerHostId, setOwnerHostId] = useLocalStorage<string | null, string | null>(
    `cleo.active-conversation:${conversationRoute}`,
    null,
    Schema.NullOr(Schema.String),
  );
  // The channel's last answered binding for this route. It outlives a switch
  // back to the session tab, so reopening the door shows its conversation at
  // once instead of an empty rail while the channel's status is read again.
  const [ownerView, setOwnerView] = useState<{
    route: string;
    hostSessionId: string;
    address: LocalContextAddress | null;
  } | null>(null);
  const owner = useMemo(() => {
    if (ownerHostId === null) return null;
    const known = ownerView?.route === conversationRoute && ownerView.hostSessionId === ownerHostId;
    return {
      hostSessionId: ownerHostId,
      address: known ? ownerView.address : null,
      answered: known,
    };
  }, [ownerHostId, ownerView, conversationRoute]);
  const selectOwner = useCallback(
    (hostSessionId: string) => {
      setOwnerHostId(hostSessionId);
      setOwnerView((current) =>
        current?.route === conversationRoute && current.hostSessionId === hostSessionId
          ? current
          : null,
      );
    },
    [conversationRoute, setOwnerHostId],
  );
  const selectSession = useCallback(() => {
    setOwnerHostId(null);
  }, [setOwnerHostId]);
  const bindOwner = useCallback(
    (hostSessionId: string, address: LocalContextAddress | null) => {
      setOwnerView((current) => {
        if (ownerHostId !== hostSessionId) return current;
        if (
          current?.route === conversationRoute &&
          current.hostSessionId === hostSessionId &&
          current.address?.environmentId === address?.environmentId &&
          current.address?.threadId === address?.threadId
        )
          return current;
        return { route: conversationRoute, hostSessionId, address };
      });
    },
    [conversationRoute, ownerHostId],
  );
  const activeConversation = useMemo(
    () => ({ owner, selectOwner, selectSession, bindOwner }),
    [owner, selectOwner, selectSession, bindOwner],
  );
  // A door never changes the World: the iMessage conversation keeps its own
  // thread's World, and only a thread with no World is Local.
  const doorShell = useThreadShell(
    owner?.address
      ? scopeThreadRef(owner.address.environmentId as never, owner.address.threadId as never)
      : null,
  );
  // W-d56f283b: a World Session thread on its thread route is that conversation.
  const routeThreadShell = useThreadShell(
    leaf?.kind === "thread"
      ? scopeThreadRef(leaf.environmentId as never, leaf.threadId as never)
      : null,
  );
  const displayedConversation = useMemo(
    () =>
      resolveRouteConversation({
        displayed: resolveDisplayedConversation({ door: owner, doorShell, activeWorldKey }),
        routeThreadShell,
        activeWorldKey,
      }),
    [owner, doorShell, activeWorldKey, routeThreadShell],
  );
  const localConversation = localMode || displayedConversation.kind === "local";
  const { addressedCoordinate, catalogCoordinate, doorSessionUnread } =
    useMemo<ShellCoordinates>(() => {
      if (sessionRoom.state.kind !== "world-ready")
        return { addressedCoordinate: null, catalogCoordinate: null, doorSessionUnread: false };
      const index = sessionRoom.state.index;
      return resolveShellCoordinates({
        displayed: displayedConversation,
        activeWorldKey,
        index,
        routeSessionId:
          leaf?.kind === "session"
            ? resolveAddressSessionId(index, leaf.sessionId, requestedAddress)
            : null,
        shells,
      });
    }, [activeWorldKey, displayedConversation, leaf, requestedAddress, shells, sessionRoom.state]);
  // A door Session the World view does not hold yet is read on its own and
  // laid over the view; the rail waits only while that read is in flight.
  const doorSessionRead = useDoorSessionEntries({
    worldKey: activeWorldKey,
    sessionId:
      doorSessionUnread && displayedConversation.kind === "world-session"
        ? displayedConversation.sessionId
        : null,
    accept: sessionRoom.acceptEntryReadback,
  });
  const doorSessionReading = doorSessionRead === "reading";
  const addressedThreadRef = useMemo(
    () =>
      addressedCoordinate === null
        ? null
        : scopeThreadRef(
            addressedCoordinate.agentEnvironmentId as never,
            addressedCoordinate.agentThreadId as never,
          ),
    [addressedCoordinate],
  );
  const addressedMessages = useThreadMessages(addressedThreadRef);
  const addressedActivities = useThreadActivities(addressedThreadRef);
  const universeKey =
    addressedCoordinate === null ? null : roomUniverseCoordinateKey(addressedCoordinate);
  const universeEntry = useRoomUniverseStore((state) =>
    universeKey === null ? undefined : state.byCoordinate[universeKey],
  );
  const catalogKey = catalogCoordinate === null ? null : roomCatalogKey(catalogCoordinate);
  const catalogEntry = useRoomUniverseStore((state) =>
    catalogKey === null ? undefined : state.catalogByRoom[catalogKey],
  );
  useRoomUniverseRefresh({
    addressedCoordinate,
    catalogCoordinate,
    grantRevision,
    worldHead: sessionRoom.state.kind === "world-ready" ? sessionRoom.state.index.head : null,
    worldCut: sessionRoom.state.kind === "world-ready" ? sessionRoom.state.index.cut : null,
    localMode: localConversation,
    worldsLoaded,
  });
  const [birthRetry, setBirthRetry] = useState(0);
  const [birthPreparation, setBirthPreparation] = useState<{
    key: string;
    status: "pending" | "ready" | "failed";
    reason?: string;
  } | null>(null);
  const birthWorld = addressedCoordinate?.worldKey;
  const birthRoom = addressedCoordinate?.roomId;
  const birthSession = addressedCoordinate?.sessionId;
  const birthEnvironment = addressedCoordinate?.agentEnvironmentId;
  const birthThread = addressedCoordinate?.agentThreadId;
  useEffect(() => {
    if (!birthWorld || !birthRoom || !birthSession || !birthEnvironment || !birthThread) return;
    const coordinate = {
      worldKey: birthWorld,
      roomId: birthRoom,
      sessionId: birthSession,
      agentEnvironmentId: birthEnvironment,
      agentThreadId: birthThread,
    };
    const key = roomUniverseCoordinateKey(coordinate);
    let current = true;
    setBirthPreparation({ key, status: "pending" });
    void initializeBoardContextBirth(coordinate).then((outcome) => {
      if (!current) return;
      setBirthPreparation(
        outcome.ok ? { key, status: "ready" } : { key, status: "failed", reason: outcome.reason },
      );
    });
    return () => {
      current = false;
    };
  }, [
    birthWorld,
    birthRoom,
    birthSession,
    birthEnvironment,
    birthThread,
    grantRevision,
    birthRetry,
  ]);
  const birthReady =
    addressedCoordinate === null ||
    (birthPreparation?.key === universeKey && birthPreparation.status === "ready");
  // Local's boundary read stands only for an addressed ungoverned session in
  // the PRIMARY environment — the walk happens on the machine owning the
  // files, and that is the primary server's machine.
  const localDraft = useComposerDraftSessionStore((store) =>
    localMode && leaf?.kind === "draft" ? store.getDraftSession(DraftId.make(leaf.draftId)) : null,
  );
  const routeLocalShell = useThreadShell(
    owner
      ? null
      : localMode && leaf?.kind === "thread"
        ? scopeThreadRef(leaf.environmentId as never, leaf.threadId as never)
        : localDraft
          ? scopeThreadRef(localDraft.environmentId, localDraft.threadId)
          : null,
  );
  const localLeafShell = owner ? doorShell : routeLocalShell;
  const localContextAddress = useMemo(
    () =>
      localConversation &&
      localLeafShell !== null &&
      (localLeafShell.worldKey ?? null) === null &&
      (localLeafShell.sessionId ?? null) === null &&
      localLeafShell.environmentId === primaryEnvironmentId
        ? {
            environmentId: String(localLeafShell.environmentId),
            threadId: localLeafShell.id,
          }
        : null,
    [localConversation, localLeafShell, primaryEnvironmentId],
  );
  const localBoundary = useLocalBoundary(localContextAddress);
  const localMessages = useThreadMessages(
    localLeafShell ? scopeThreadRef(localLeafShell.environmentId, localLeafShell.id) : null,
  );
  const localActivities = useThreadActivities(
    localLeafShell ? scopeThreadRef(localLeafShell.environmentId, localLeafShell.id) : null,
  );
  const requestLocalDirectory = useCallback(
    (directory: string, cursor?: string, source?: "codebase" | "prepared-copy") => {
      if (localContextAddress)
        return browseLocalDirectory(localContextAddress, directory, cursor, source);
    },
    [localContextAddress],
  );
  const universe = useMemo<RoomUniverseProjection>(() => {
    if (!worldsLoaded) return { state: "absent", reason: "loading" };
    // A door onto another World's conversation names that World, in Local
    // too; the front never switches to follow it.
    if (displayedConversation.kind === "other-world")
      return { state: "absent", reason: "other-world", worldKey: displayedConversation.worldKey };
    // The door's channel has not answered: its conversation is unknown, so the
    // rail waits rather than reading it as Local.
    if (displayedConversation.kind === "pending") return { state: "absent", reason: "loading" };
    if (localConversation) {
      if (localBoundary === null) return { state: "absent", reason: "local" };
      const rootPage = localBoundary.pages[""];
      return {
        state: "local-boundary",
        root: localBoundary.root,
        loading: localBoundary.state === "loading",
        reason: rootPage?.state === "unavailable" ? rootPage.reason : null,
        selectionReason: localBoundary.reason,
        selectionPending: localBoundary.pending,
        ...(localBoundary.selection ? { selection: localBoundary.selection } : {}),
        members: Object.values(localBoundary.pages).flatMap((page) =>
          page.state === "members" ? page.members : [],
        ),
        directories: localBoundary.pages,
        hasPreparedCopy: localBoundary.hasPreparedCopy,
        preparedCopy: localBoundary.preparedCopy,
        truncatedAt: null,
      };
    }
    // The door's Session is being read on its own (useDoorSessionEntries):
    // loading, never an ungoverned reading of a World conversation.
    if (doorSessionReading) return { state: "absent", reason: "loading" };
    if (activeWorldKey === null) return { state: "absent", reason: "no-world" };
    if (addressedCoordinate !== null) {
      // The room universe belongs to the room; its device-local roots
      // come from the agent's own environment.
      const binding = bindingsByEnvironment.get(addressedCoordinate.agentEnvironmentId as never)?.[
        activeWorldKey
      ];
      // Fetched groups render whenever the store holds them — an incomplete
      // catalog is per-group truth, not a reason to fall back to the
      // skeleton (CB2). Selection marks ride only a complete entry; moves
      // already gate on complete.
      // RL-CAN-26: a large room renders from its summary and its shown pages.
      // A refresh invalidates authority, not the catalog's visible furniture.
      // Move/Send still consult universeEntry, never this retained display.
      const displayEntry = roomUniverseDisplayEntry(universeEntry);
      if (displayEntry?.state === "selection") return projectStoredSelection(displayEntry);
      if (displayEntry?.state === "paged") {
        return projectPagedRoomUniverse({
          worldKey: activeWorldKey,
          roomId: addressedCoordinate.roomId,
          summary: displayEntry.summary,
          pages: displayEntry.pages,
        });
      }
      const addressedGroups = renderableUniverseGroups(displayEntry);
      if (addressedGroups !== null) {
        return projectRoomUniverseMembers({
          worldKey: activeWorldKey,
          roomId: addressedCoordinate.roomId,
          fetchedGroups: addressedGroups,
          effectiveSelection:
            displayEntry?.state === "complete" ? new Set(displayEntry.selection.memberIds) : null,
          ...(displayEntry?.state === "complete"
            ? { includedKnowledge: new Set(workingContextMemberIds(displayEntry.selection) ?? []) }
            : {}),
        });
      }
      return projectRoomUniverseSkeleton({
        worldKey: activeWorldKey,
        roomId: addressedCoordinate.roomId,
        overlay: "session",
        codeRootLabel: skeletonCodeRootLabel(binding),
        filesRoot: binding?.filesRoot ?? null,
      });
    }
    // No agent session addressed, but the room is known: the catalog
    // belongs to the room (CX15), so it stays visible read-only — with no
    // overlay and no move doorway. The enumerating environment is the one
    // the catalog read reaches: the primary server, whose World workspace
    // binding labels the skeleton while the members load.
    if (catalogCoordinate !== null) {
      const catalogGroups = renderableUniverseGroups(roomCatalogDisplayEntry(catalogEntry));
      if (catalogGroups !== null) {
        return projectRoomUniverseMembers({
          worldKey: activeWorldKey,
          roomId: catalogCoordinate.roomId,
          fetchedGroups: catalogGroups,
          effectiveSelection: null,
        });
      }
      const binding =
        primaryEnvironmentId === null
          ? undefined
          : bindingsByEnvironment.get(primaryEnvironmentId)?.[activeWorldKey];
      return projectRoomUniverseSkeleton({
        worldKey: activeWorldKey,
        roomId: catalogCoordinate.roomId,
        overlay: "none",
        codeRootLabel: skeletonCodeRootLabel(binding),
        filesRoot: binding?.filesRoot ?? null,
      });
    }
    // Neither an addressed session nor a grounded room to key the catalog
    // (the Session index is still loading, the Session is unknown, or its top is
    // orphaned): a typed absence, never a fake skeleton.
    return { state: "absent", reason: "ungoverned-session" };
  }, [
    activeWorldKey,
    addressedCoordinate,
    bindingsByEnvironment,
    catalogCoordinate,
    catalogEntry,
    displayedConversation,
    doorSessionReading,
    localBoundary,
    localConversation,
    primaryEnvironmentId,
    universeEntry,
    worldsLoaded,
  ]);
  // The one doorway's dispatch (CB6 riding CB5): every gesture — a member
  // click, a folder toggle, a group toggle, a drop — is ONE atomic batch
  // through one door call holding one CAS window. A refusal moves nothing
  // (CX08), so there is no partial landing to narrate; the queue only keeps
  // concurrent gestures from racing each other's successor receipts.
  const moveMembers = useCallback(
    (
      moves: ReadonlyArray<ContextMemberMove>,
      options?: {
        readonly replace?: boolean;
        readonly restoreFromResolutionId?: string;
        readonly clearGroups?: ReadonlyArray<string>;
      },
    ) => {
      if (localContextAddress)
        return moveLocalContext(localContextAddress, {
          expressions: moves.map((move) => ({
            memberId: move.memberId,
            kind: move.kind ?? "member",
            selected: move.direction === "in",
            ...(move.kind === "folder"
              ? { delivery: "reference" as const }
              : move.delivery === undefined
                ? {}
                : { delivery: move.delivery }),
          })),
          ...options,
        }).catch((error) => {
          toastManager.add({
            type: "error",
            title: "Context update needs attention",
            description: error instanceof Error ? error.message : "Review the selection and retry.",
          });
        });
      if (
        !birthReady ||
        addressedCoordinate === null ||
        (universeEntry?.state !== "complete" &&
          universeEntry?.state !== "paged" &&
          universeEntry?.state !== "selection")
      ) {
        toastManager.add({
          type: "error",
          title: "Context is not ready",
          description: "Wait for this session’s context to load, then retry.",
        });
        return;
      }
      const store = useRoomUniverseStore.getState();
      // Delivery applies to admitted content. An exclusion retains its typed
      // identity but carries no delivery command through the World move door.
      const worldMoves = moves.map(({ delivery, ...move }) => ({
        ...move,
        ...(move.direction === "in" && delivery !== undefined ? { delivery } : {}),
      }));
      const single = worldMoves.length === 1 ? worldMoves[0] : undefined;
      // The store reserves this gesture's place immediately, shared with Send
      // and every other component that changes this session's selection.
      const work =
        single && !options?.replace && !options?.clearGroups?.length
          ? store.moveMember({ ...addressedCoordinate, ...single })
          : store.moveMembers({ ...addressedCoordinate, moves: worldMoves, ...options });
      return work
        .then((outcome) => {
          if (outcome._tag === "Refused")
            toastManager.add({ type: "error", ...contextChangeRefusalCopy(outcome.reason) });
        })
        .catch((error: unknown) => {
          toastManager.add({
            type: "error",
            title: "Context update failed",
            description: error instanceof Error ? error.message : "Review the selection and retry.",
          });
        });
    },
    [addressedCoordinate, universeEntry, birthReady, localContextAddress],
  );
  const requestPage = useCallback<RequestPageDoorway>(
    (request) => {
      if (addressedCoordinate !== null) {
        if (
          request.directory !== undefined &&
          (request.group === "root-codebase" || request.group === "world-files")
        )
          void useRoomUniverseStore
            .getState()
            .loadDirectory(addressedCoordinate, request.group, request.directory, request.cursor);
        else void useRoomUniverseStore.getState().loadPage(addressedCoordinate, request);
      }
    },
    [addressedCoordinate],
  );
  const movePredicate = useCallback<MovePredicateDoorway>(
    (predicate) => {
      if (!birthReady || addressedCoordinate === null || universeEntry?.state !== "paged") return;
      void useRoomUniverseStore
        .getState()
        .movePredicate(addressedCoordinate, predicate)
        .then((outcome) => {
          if (outcome._tag === "Refused")
            toastManager.add({
              type: "error",
              title: "Context update needs attention",
              description: outcome.reason,
            });
        });
    },
    [addressedCoordinate, universeEntry, birthReady],
  );

  // WSET-CAN-10: the proposal zone's ONE read — the addressed World session's
  // recorded proposal for its next turn. It reads again when the thread's
  // turn completes (the shell's latestTurn, a pure read in contextDelivery's
  // style) and when the rail's own refresh settles a new universe entry —
  // the outcome of every trigger useRoomUniverseRefresh drives (grant
  // revision, the watched source and selection revisions, the head/cut
  // fallback, the library/CRM/online events) and of every landed move. The
  // display entry keeps its identity while a refresh is loading, so one
  // refresh is one read. No timer. A Local conversation reads nothing. A
  // refused read is the typed null. Looking writes nothing (DL3).
  const addressedShell = useThreadShell(addressedThreadRef);
  const proposalTurnKey = completedTurnKey(addressedShell);
  const proposalRefreshEntry = roomUniverseDisplayEntry(universeEntry);
  const [proposalRead, setProposalRead] = useState<{
    readonly key: string;
    readonly proposal: ContextProposalProjection | null;
  } | null>(null);
  useEffect(() => {
    if (
      localConversation ||
      universeKey === null ||
      !birthWorld ||
      !birthRoom ||
      !birthSession ||
      !birthEnvironment ||
      !birthThread
    )
      return;
    let current = true;
    void readContextProposal({
      worldKey: birthWorld,
      roomId: birthRoom,
      sessionId: birthSession,
      agentEnvironmentId: birthEnvironment,
      agentThreadId: birthThread,
    }).then((read) => {
      if (!current) return;
      setProposalRead({
        key: universeKey,
        proposal: read.state === "ready" ? read.proposal : null,
      });
    });
    return () => {
      current = false;
    };
  }, [
    localConversation,
    universeKey,
    birthWorld,
    birthRoom,
    birthSession,
    birthEnvironment,
    birthThread,
    grantRevision,
    proposalTurnKey,
    proposalRefreshEntry,
  ]);
  const proposal =
    !localConversation && universeKey !== null && proposalRead?.key === universeKey
      ? proposalRead.proposal
      : undefined;
  // WSET-CAN-06: a mark is the one relevance-mark write and NO move — it
  // never reaches moveMembers. Acceptance is the existing moveMembers with
  // `proposal:<id>` on every move (the rail composes the batch).
  const markRelevance = useCallback(
    (item: ContextProposalItemRef, mark: RelevanceMarkKind) => {
      if (!birthWorld || proposal === undefined || proposal === null) return;
      return markContextProposalRelevance({
        worldKey: birthWorld,
        proposalId: proposal.proposalId,
        ...(item.memberId === undefined ? {} : { memberId: item.memberId }),
        ...(item.tag === undefined ? {} : { tag: item.tag }),
        mark,
      }).then((outcome) => {
        if (!outcome.ok)
          toastManager.add({
            type: "error",
            title: "Relevance mark refused",
            description: outcome.reason,
          });
      });
    },
    [birthWorld, proposal],
  );
  // The ONE doorway's gate (CB6): a member gesture, a proposal acceptance and
  // a drop all pass the same readiness test and reach the same dispatch.
  const moveDoorway =
    localBoundary?.selection ||
    (birthReady &&
      (universeEntry?.state === "complete" ||
        universeEntry?.state === "paged" ||
        universeEntry?.state === "selection"))
      ? moveMembers
      : undefined;

  // CB19: the open doorway under the ruled pointer grammar — a member opens
  // as one more Canvas tab (singleton by member id; D-CT3), and single-click
  // fronts an already-open tab. With no addressed session the rail receives
  // no doorway, so the verb refuses typed (G5). Opening writes nothing to
  // the selection. CB23: the tab lands in the ROUTE LEAF's deck — the only
  // deck any mounted component renders — never the ?a= address's (the
  // seatCount law: the address overlay never feeds geometry).
  const memberCanvasScope = useMemo(
    () =>
      resolveMemberCanvasScope({
        // #16b RULED 2026-08-30: the addressed world view IS the session —
        // born as a record before any message — so opening a member never
        // waits on an agent execution. Only the catalog-only rail (no room
        // addressed) keeps the typed G5 refusal.
        worldKey:
          activeWorldKey !== null && sessionRoom.state.kind === "world-ready"
            ? activeWorldKey
            : null,
        leafSessionId: leaf?.kind === "session" ? leaf.sessionId : null,
      }),
    [activeWorldKey, leaf, sessionRoom.state.kind],
  );
  const musicScope =
    memberCanvasScope ??
    (localLeafShell === null
      ? null
      : scopeThreadRef(localLeafShell.environmentId, localLeafShell.id));
  // Dated 2026-09-21: the Files board opens in the same deck a member opens
  // in (CB23: the route leaf's deck), fronted as the `files` singleton.
  const openFilesBoard = useCallback(() => {
    if (memberCanvasScope === null) return;
    useBoardDeckStore.getState().open(memberCanvasScope, "files");
  }, [memberCanvasScope]);
  const openMember = useCallback(
    (memberId: string, label?: string, canvasRevision?: OpenMemberRequest["canvasRevision"]) => {
      if (memberCanvasScope === null) return;
      useBoardDeckStore.getState().openMember(memberCanvasScope, {
        memberId,
        memberType: roomMemberType(memberId),
        ...(canvasRevision ? { canvasRevision } : {}),
        worldKey: memberCanvasScope.worldKey,
        label: label ?? memberId.slice(memberId.indexOf(":") + 1),
      });
    },
    [memberCanvasScope],
  );
  // Dated 2026-09-21: the Files board's seat at the ONE doorway — the same
  // universe fold, move doorway, page reader and member opener the rail
  // receives below, so a move from the board IS a move from the rail (O14).
  const filesBoardDoorway = useMemo<FilesBoardDoorway>(
    () => ({
      universe,
      onMoveMembers: moveDoorway,
      onRequestPage:
        universeEntry?.state === "paged" || universeEntry?.state === "selection"
          ? requestPage
          : undefined,
      onOpenMember: memberCanvasScope === null ? undefined : openMember,
    }),
    [memberCanvasScope, moveDoorway, openMember, requestPage, universe, universeEntry?.state],
  );
  // CVC2: a conversation card (the Canvas Cleo just made) opens through the
  // same door as the rail, on the deck that is actually rendered.
  useEffect(
    () =>
      onOpenMemberRequest((request) => {
        openMember(request.memberId, request.label, request.canvasRevision);
        request.onOpened?.(
          memberCanvasScope === null
            ? null
            : { ...memberCanvasScope, board: `member:${request.memberId}` },
        );
      }),
    [openMember, memberCanvasScope],
  );
  // W-a67b0503: the World remembers the session its own index confirms the
  // person stands in, so the next door into it (the bell, the strip) opens it
  // at once instead of reading the whole index.
  const confirmedRoom = confirmedWorldRoom(
    sessionRoom.state.kind === "world-ready" &&
      memberCanvasScope !== null &&
      sessionRoom.state.worldKey === memberCanvasScope.worldKey
      ? sessionRoom.state.index
      : null,
    memberCanvasScope,
  );
  const confirmedRoomWorld = confirmedRoom?.worldKey ?? null;
  const confirmedRoomPath = confirmedRoom?.path ?? null;
  useEffect(() => {
    if (confirmedRoomWorld !== null && confirmedRoomPath !== null)
      rememberWorldRoom(confirmedRoomWorld, confirmedRoomPath);
  }, [confirmedRoomWorld, confirmedRoomPath]);
  const navigateNotification = useNavigate();
  const consumedNotification = useRef<string | null>(null);
  const routeNotice = typeof routeSearch["notice"] === "string" ? routeSearch["notice"] : null;
  // A notice whose route the World's own recovery dropped is still owed to
  // that World's landing session (W-a67b0503).
  const owedNotice =
    routeNotice === null ? owedWorldNotice(memberCanvasScope?.worldKey ?? null) : null;
  const notice = routeNotice ?? owedNotice;
  const noticeWorld =
    routeNotice === null
      ? (memberCanvasScope?.worldKey ?? null)
      : typeof routeSearch["world"] === "string"
        ? routeSearch["world"]
        : null;
  useEffect(() => {
    if (!notice) {
      consumedNotification.current = null;
      return;
    }
    // A cross-World route can render while its World is still loading. Never
    // open the target into the previous World's deck or context selection.
    if (
      !memberCanvasScope ||
      memberCanvasScope.worldKey !== noticeWorld ||
      sessionRoom.state.kind !== "world-ready" ||
      sessionRoom.state.worldKey !== noticeWorld
    )
      return;
    const key = `${noticeWorld}:${memberCanvasScope.sessionId}:${notice}`;
    if (consumedNotification.current === key) return;
    const destination = parseNotificationDestination(notice);
    consumedNotification.current = key;
    settleWorldNotice();
    if (destination?.kind === "decisions") {
      // W-a67b0503: the decisions door opens the requests waiting on the
      // person, and the one the notification named. The front and the
      // address both stand over the session's deck restore, which can land
      // after this and remount the Inbox (a first entry on a new profile).
      const scope = memberCanvasScope;
      requestDeckFront(scope, "inbox");
      openWorldViewInboxRail("decisions", destination.id, {
        scope: { worldKey: scope.worldKey, sessionId: scope.sessionId },
        stands: () => deckFrontStands(scope, "inbox"),
      });
    } else if (destination) {
      openMember(
        `world-vfs:crm/${destination.kind === "contact" ? "contacts" : "companies"}/${destination.id}`,
        destination.kind === "contact" ? "Contact" : "Company",
      );
    }
    // Consume the view address once. Reload/back never replays an action.
    void navigateNotification({
      to: "/session/$sessionId",
      params: { sessionId: memberCanvasScope.sessionId },
      replace: true,
      search: (prior) => {
        const { notice: _notice, ...rest } = prior;
        return rest;
      },
    });
  }, [notice, noticeWorld, memberCanvasScope, sessionRoom.state, openMember, navigateNotification]);
  const focusMember = useCallback(
    (memberId: string) => {
      if (memberCanvasScope === null) return;
      const store = useBoardDeckStore.getState();
      const panel = selectThreadBoardDeckState(store.byThreadKey, memberCanvasScope);
      const surfaceId = `member:${memberId}`;
      if (panel.surfaces.some((surface) => surface.id === surfaceId)) {
        store.activateSurface(memberCanvasScope, surfaceId);
      }
    },
    [memberCanvasScope],
  );

  // Only non-code World Files use the native folder rebind door here. The
  // Codebase root follows the World's bound default project, re-selected in
  // Settings → Connections (2026-08-28 ruling). A successful Files rebind
  // re-reads the universe through the same door.
  const persistServerSettings = useAtomCommand(serverEnvironment.updateSettings);
  const rebindEnvironmentId = useMemo(() => {
    if (addressedCoordinate !== null) {
      return addressedCoordinate.agentEnvironmentId === String(primaryEnvironmentId)
        ? primaryEnvironmentId
        : null;
    }
    return primaryEnvironmentId;
  }, [addressedCoordinate, primaryEnvironmentId]);
  const canRebindWorldFiles = isElectron && rebindEnvironmentId !== null && activeWorldKey !== null;
  const rebindWorldFiles = useCallback(async () => {
    if (rebindEnvironmentId === null || activeWorldKey === null) return;
    const worldKey = activeWorldKey;
    const binding = bindingsByEnvironment.get(rebindEnvironmentId)?.[worldKey];
    const picked = await readLocalApi()?.dialogs.pickFolder({
      initialPath: binding?.filesRoot ?? null,
    });
    if (typeof picked !== "string" || picked.length === 0 || picked === binding?.filesRoot) {
      return;
    }
    await persistServerSettings({
      environmentId: rebindEnvironmentId,
      input: { patch: { worldWorkspaceBindings: { [worldKey]: { filesRoot: picked } } } },
    });
    const store = useRoomUniverseStore.getState();
    if (addressedCoordinate !== null) {
      void store.refresh(addressedCoordinate);
    } else if (catalogCoordinate !== null) {
      void store.refreshCatalog(catalogCoordinate);
    }
  }, [
    activeWorldKey,
    addressedCoordinate,
    bindingsByEnvironment,
    catalogCoordinate,
    persistServerSettings,
    rebindEnvironmentId,
  ]);

  // The context rail's fold inputs (WS7): the shell owns the rail (CX15), so
  // the shell derives the shared ladder's inputs — the measured row, the
  // addressed lineage's rail count (the same pure seat projection the room
  // renders from), and both committed rail widths. All derivation, no second
  // store; the rail folds to a rib by width law, never a breakpoint. Level 0
  // of a seated room is the root session rail (principal ruling 2026-08-26): fixed,
  // unfoldable furniture the ladder must reserve, so the seat count splits
  // into the ego's width and the NESTED rail count the fit formula takes.
  const rowRef = useRef<HTMLDivElement>(null);
  const rowWidth = useStageWidth(rowRef);
  const [storedRailWidth] = useLocalStorage(
    AGENT_RAIL_WIDTH_STORAGE_KEY,
    AGENT_RAIL_DEFAULT_WIDTH,
    Schema.Finite,
  );
  const agentRailPx = Math.min(
    AGENT_RAIL_MAX_WIDTH,
    Math.max(AGENT_RAIL_MIN_WIDTH, storedRailWidth),
  );
  const [storedPrimaryRailWidth] = useLocalStorage(
    PRIMARY_AGENT_RAIL_WIDTH_STORAGE_KEY,
    PRIMARY_AGENT_RAIL_DEFAULT_WIDTH,
    Schema.Finite,
  );
  // The ladder's rail count is the LEAF lineage's — the same levels the room
  // physically renders. The `?a=` address fronts a folded seat's voice inside
  // a host rail; it never adds or removes a rail, so it must not shrink the
  // count the context rail folds against (an address-fed count under-reserved
  // the room's spine pixels whenever an ancestor was fronted).
  const seatCount = useMemo(() => {
    if (
      leaf?.kind !== "session" ||
      activeWorldKey === null ||
      sessionRoom.state.kind !== "world-ready"
    ) {
      return 0;
    }
    return projectWorldSessionLineage(sessionRoom.state.index, leaf.sessionId).length;
  }, [activeWorldKey, leaf, sessionRoom.state]);
  // A room the shell holds no World lineage for (Local, a draft) still seats
  // a Cleo Panel; it says so itself, with its nested rail count.
  const roomSeatedRails = useDeskWidthStore((state) => state.seatedRails);
  const cleoSeated = seatCount > 0 || roomSeatedRails !== null;
  const totalRails = seatCount > 0 ? seatCount - 1 : (roomSeatedRails ?? 0);
  // Widen Cleo (dated 2026-10-07): one plan over the whole dock row decides
  // the column the context law reserves for Cleo and whether the dock yields
  // to her; the room grows her into free deck space above that column. With
  // Widen off the plan reserves her fixed column alone and folds nothing new.
  // The stored switch: while a Context open holds, the plan pauses it itself
  // and also reads what the dock would do without the hold.
  const cleoWide = useCleoWideSetting();
  const frameWidth = useDeskWidthStore((state) => state.frameWidth);
  const dockPreferredPx = useDeskWidthStore((state) => state.dockPreferredPx);
  // The CONTEXT rib's open (dated 2026-10-07) rides the same plan: while it
  // holds, the dock and then Cleo's column yield so the rail can stand.
  const contextOpen = useDeskWidthStore((state) => state.contextOpen);
  const deskPlan = useMemo(
    () =>
      cleoSeated
        ? planDeskWidth({
            frameWidth,
            preferredPx: storedPrimaryRailWidth,
            wide: cleoWide,
            totalRails,
            agentRailPx,
            dockPreferredPx,
            hasContextRail: true,
            contextOpen,
          })
        : null,
    [
      agentRailPx,
      cleoSeated,
      cleoWide,
      contextOpen,
      dockPreferredPx,
      frameWidth,
      storedPrimaryRailWidth,
      totalRails,
    ],
  );
  const rootSessionRailPx = deskPlan?.basePx ?? 0;
  // Published before paint, so the room and the dock never paint a frame of
  // the previous plan beside the context rail's new column.
  useLayoutEffect(() => {
    useDeskWidthStore.getState().publishPlan(deskPlan);
  }, [deskPlan]);
  useLayoutEffect(() => () => useDeskWidthStore.getState().publishPlan(null), []);
  // RL-CAN-01: the rail names the session whose selection it shows, by the
  // name the Cleo pane shows for it: the addressed thread's own title, then the
  // Session's World-index label (the left dock's chip name), never another
  // conversation's (W-a2f3edff, railSessionName). The status is the SAME
  // per-thread fold the left rail wears (FD-CAN-39) — one fold, no second
  // status. Read, never stored.
  const threadInputs = useRootSessionThreadInputs();
  const railSession = useMemo(() => {
    if (localContextAddress && localLeafShell)
      return {
        label: localLeafShell.title,
        status: null,
        sends: localMessages.flatMap((message) =>
          message.role === "user" && message.contextResolution
            ? [
                {
                  messageId: String(message.id),
                  resolution: message.contextResolution,
                  delivery: projectContextDelivery(message, localActivities),
                  workingContext: projectWorkingContextDelivery(message, localActivities),
                },
              ]
            : [],
        ),
      };
    if (addressedCoordinate === null || sessionRoom.state.kind !== "world-ready") return undefined;
    const slice = sessionRoom.state.index.sessions.find(
      (entry) => entry.sessionId === addressedCoordinate.sessionId,
    );
    const thread = threadInputs.find(
      (entry) =>
        entry.environmentId === addressedCoordinate.agentEnvironmentId &&
        entry.threadId === addressedCoordinate.agentThreadId,
    );
    return {
      label: railSessionName({
        threadTitle: thread?.title,
        sessionLabel: slice?.label,
        sessionId: addressedCoordinate.sessionId,
      }),
      status: thread?.status ?? null,
      sends: addressedMessages.flatMap((message) =>
        message.role === "user" && message.contextResolution !== undefined
          ? [
              {
                messageId: String(message.id),
                resolution: message.contextResolution,
                delivery: projectContextDelivery(message, addressedActivities),
                workingContext: projectWorkingContextDelivery(message, addressedActivities),
              },
            ]
          : [],
      ),
    };
  }, [
    localContextAddress,
    localLeafShell,
    localMessages,
    localActivities,
    addressedActivities,
    addressedCoordinate,
    addressedMessages,
    sessionRoom.state,
    threadInputs,
  ]);

  // The Electron shell's drop guard (CB7): a stray Finder drop must never
  // navigate the sandboxed renderer. Claimed drags (a boundary zone, the
  // theme dialog) pass untouched; unclaimed ones are neutralized here.
  useEffect(() => installDocumentDropGuard(document), []);

  return (
    <ActiveConversationContext.Provider value={activeConversation}>
      <BoardCompanionProvider scope={musicScope}>
        <CrmContextCoordinate.Provider value={addressedCoordinate}>
          <ContextCollectionHost />
          <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-transparent text-foreground">
            {/* FD-CAN-29/30: no opaque ground here — the ringed cards float on the room
          ground painted by the frame row's underlay beneath them. */}
            {leaf?.kind === "session" ? null : <NeucleosNestedSessionStrip />}
            <div
              className="flex min-h-0 min-w-0 w-full flex-1"
              data-cleo-session-territory=""
              ref={rowRef}
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-cleo-slice-center="">
                {/* Dated 2026-09-21: the deck's Files board reads the rail's own
                    universe and doorway here (O14: one doorway, many hands). */}
                <FilesBoardDoorwayContext.Provider value={filesBoardDoorway}>
                  <Outlet />
                </FilesBoardDoorwayContext.Provider>
              </div>
              <NeucleosContextRail
                musicScope={musicScope}
                renderAccountControls={(wide) => <DockAccountControls wide={wide} />}
                density={contextRailDensity}
                onBindWorldFiles={openWorldRoomConnections}
                onFocusMember={memberCanvasScope === null ? undefined : focusMember}
                onMoveMembers={moveDoorway}
                proposal={proposal}
                contextSuggestions={
                  birthReady && addressedCoordinate && addressedThreadRef ? (
                    <NeucleosContextSuggestions
                      key={universeKey}
                      coordinate={addressedCoordinate}
                      threadRef={addressedThreadRef}
                      selectionDigest={
                        proposalRefreshEntry && "selection" in proposalRefreshEntry
                          ? proposalRefreshEntry.selection.selectionDigest
                          : undefined
                      }
                      labels={Object.fromEntries(
                        ("groups" in universe ? universe.groups : []).flatMap((group) =>
                          (group.members ?? []).map((member) => [member.id, member.label]),
                        ),
                      )}
                    />
                  ) : undefined
                }
                onAcceptProposal={proposal === undefined ? undefined : moveDoorway}
                onMarkRelevance={proposal === undefined ? undefined : markRelevance}
                onBrowseLocal={localContextAddress ? requestLocalDirectory : undefined}
                onMovePredicate={
                  birthReady && universeEntry?.state === "paged" ? movePredicate : undefined
                }
                onOpenMember={memberCanvasScope === null ? undefined : openMember}
                onOpenFilesBoard={memberCanvasScope === null ? undefined : openFilesBoard}
                onRebindWorldFiles={canRebindWorldFiles ? rebindWorldFiles : undefined}
                onRequestPage={
                  universeEntry?.state === "paged" || universeEntry?.state === "selection"
                    ? requestPage
                    : undefined
                }
                selectionCoordinate={addressedCoordinate ?? undefined}
                localContextAddress={localContextAddress ?? undefined}
                selectionPreparation={
                  birthReady
                    ? undefined
                    : {
                        status:
                          birthPreparation?.key === universeKey &&
                          birthPreparation.status === "failed"
                            ? "failed"
                            : "pending",
                        reason:
                          birthPreparation?.key === universeKey
                            ? birthPreparation.reason
                            : undefined,
                        retry: () => setBirthRetry((value) => value + 1),
                      }
                }
                session={railSession}
                stage={{ agentRailPx, rootSessionRailPx, rowWidth, totalRails }}
                universe={universe}
              />
            </div>
            {birthReady &&
            addressedCoordinate !== null &&
            (universeEntry?.state === "complete" || universeEntry?.state === "selection") ? (
              <BoundaryDropZones
                inScopeMemberIds={universeEntry.selection.memberIds}
                roomId={addressedCoordinate.roomId}
                onMoveMembers={moveMembers}
                onRefuse={(reason) =>
                  toastManager.add({
                    type: "error",
                    title: "Context move refused",
                    description: reason,
                  })
                }
                worldKey={addressedCoordinate.worldKey}
              />
            ) : null}
          </SidebarInset>
        </CrmContextCoordinate.Provider>
      </BoardCompanionProvider>
    </ActiveConversationContext.Provider>
  );
}

function ChatRouteLayout() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const frame = chatLayoutFrameKind(pathname);
  // Every room leaf sits behind the governed-room gate: a selected World that
  // has not read back names itself in place of the leaf, so no local thread or
  // draft is ever shown wearing that World's frame. Non-room leaves (settings,
  // pull requests) pass through, which is what keeps Settings reachable.
  if (frame === "bare") {
    return (
      <GovernedWorldViewGate pathname={pathname}>
        <ChatRouteGlobalShortcuts />
        <WorldNotAnsweringNotice />
        <Outlet />
      </GovernedWorldViewGate>
    );
  }
  return (
    <GovernedWorldViewGate pathname={pathname}>
      <ChatRouteGlobalShortcuts />
      <WorldNotAnsweringNotice />
      <ChatSessionFrame pathname={pathname} />
    </GovernedWorldViewGate>
  );
}

export const Route = createFileRoute("/_chat")({
  beforeLoad: async ({ context }) => {
    if (!shellOpensFor(context.authGateState.status)) {
      throw redirect({ to: "/pair", replace: true });
    }
  },
  component: ChatRouteLayout,
});
